/**
 * PublishBar Component
 *
 * Draft status and the three actions that change stored state.
 *
 * Publish and reset both ask first, for different reasons: publish changes what
 * every player at the site sees, and reset removes the draft as well as the
 * published record — which is not obvious from a button labelled "Reset".
 *
 * Requirements: 2.4, 2.7, 5.9, 6.2, 6.3, 6.6, 6.7, 6.8, 7.2
 */

import { useCallback, useState } from 'react';
import './PublishBar.css';

/** Which confirmation is open, if any. Only one at a time. */
type PendingConfirmation = 'publish' | 'reset' | null;

export interface PublishBarProps {
  readonly hasPendingDraft: boolean;
  readonly hasUnsavedChanges: boolean;
  /** When the working brand was last written, or null when nothing is stored. */
  readonly updatedAt: string | null;
  readonly canSave: boolean;
  readonly isSaving: boolean;
  readonly isPublishing: boolean;
  /**
   * True while any action that writes stored branding is in flight.
   *
   * All three actions are disabled together, because they write the same two
   * records: a save that resolved after a reset would resurrect the brand the
   * operator had just cleared.
   */
  readonly isMutating: boolean;
  /** Why saving is unavailable, shown so a disabled button is not a dead end. */
  readonly saveDisabledReason: string | null;
  /**
   * Whether the publish control is offered at all.
   *
   * Publishing is gated behind advanced mode while the feature rolls out. When
   * hidden, the notice about what publishing sends is hidden with it.
   */
  readonly showPublish: boolean;
  readonly onSave: () => void;
  readonly onPublish: () => void;
  readonly onReset: () => void;
}

/** Absolute local date and time; branding changes are rare, so "3 days ago" is vaguer than useful. */
function formatUpdatedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export function PublishBar({
  hasPendingDraft,
  hasUnsavedChanges,
  updatedAt,
  canSave,
  isSaving,
  isPublishing,
  isMutating,
  saveDisabledReason,
  showPublish,
  onSave,
  onPublish,
  onReset,
}: PublishBarProps): JSX.Element {
  const [confirming, setConfirming] = useState<PendingConfirmation>(null);

  const confirm = useCallback((action: () => void) => {
    setConfirming(null);
    action();
  }, []);

  return (
    <div className="publish-bar">
      <div className="publish-bar__status">
        {hasPendingDraft && (
          <span className="publish-bar__badge">Draft</span>
        )}
        {updatedAt !== null && (
          <span className="publish-bar__updated">
            Last changed {formatUpdatedAt(updatedAt)}
          </span>
        )}
      </div>

      <div className="publish-bar__actions">
        <button
          type="button"
          className="publish-bar__button publish-bar__button--quiet"
          onClick={() => setConfirming('reset')}
          disabled={isMutating}
        >
          Reset to Default
        </button>

        <button
          type="button"
          className="publish-bar__button"
          onClick={onSave}
          disabled={!canSave || isSaving || isMutating}
        >
          {isSaving ? 'Saving…' : 'Save Draft'}
        </button>

        {showPublish && (
          <button
            type="button"
            className="publish-bar__button publish-bar__button--primary"
            onClick={() => setConfirming('publish')}
            disabled={!hasPendingDraft || isPublishing || isMutating}
          >
            {isPublishing ? 'Publishing…' : 'Publish Changes'}
          </button>
        )}
      </div>

      {saveDisabledReason !== null && (
        <p className="publish-bar__blocked">{saveDisabledReason}</p>
      )}

      {showPublish && hasPendingDraft && hasUnsavedChanges && (
        <p className="publish-bar__notice" role="status">
          Publishing sends the saved draft. Your unsaved changes will not be included
          until you save them.
        </p>
      )}

      {showPublish && confirming === 'publish' && (
        <div className="publish-bar__confirm" role="alertdialog" aria-label="Confirm publish">
          <p className="publish-bar__confirm-text">
            Publish this brand? Every player at this site will see it straight away.
          </p>
          <div className="publish-bar__actions">
            <button
              type="button"
              className="publish-bar__button publish-bar__button--primary"
              onClick={() => confirm(onPublish)}
            >
              Yes, publish
            </button>
            <button
              type="button"
              className="publish-bar__button"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirming === 'reset' && (
        <div
          className="publish-bar__confirm publish-bar__confirm--danger"
          role="alertdialog"
          aria-label="Confirm reset"
        >
          <p className="publish-bar__confirm-text">
            Reset this site to the Toptracer default? This removes both the published
            brand and the draft, and cannot be undone.
          </p>
          <div className="publish-bar__actions">
            <button
              type="button"
              className="publish-bar__button publish-bar__button--danger"
              onClick={() => confirm(onReset)}
            >
              Yes, reset
            </button>
            <button
              type="button"
              className="publish-bar__button"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
